import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import _ from "lodash";
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from "react";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDropdownSourceContext } from "@ryde/redux";
import { IDropdownItem, IInput, Log, ValidationHelpers } from "@ryde/shared";

import { getDropdownValuesLabels } from "../../../dynamic-forms";

// name will be relative to the control of form passed to the input
// can be used to create independent nested forms
export const MultiSelectBoundInputByControl: FC<IInput> = ({
  control,
  name,
  formLabel,
  formValidation,
  formAttributes,
  className,
}) => {
  const { dropItemsSource, customDropdownItemsFilter, disableSorting } =
    formAttributes ?? {};

  const { t } = useTranslation();
  const [isLoading, startTransition] = useTransition();
  const { getDropdownItems } = useDropdownSourceContext();
  const [dropDownItems, setDropDownItems] = useState<
    IDropdownItem[] | undefined
  >(undefined);

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      const items = await getDropdownItems?.(dropItemsSource || "unknown");
      setDropDownItems(items || []);
    };

    if (dropItemsSource) {
      startTransition(() => {
        getItems();
      });
    }
  }, [getDropdownItems, dropItemsSource]);

  const initial: number[] = []; // getValues(name as string);

  if (initial && !_.isArray(initial)) {
    Log.error(t("errors.multiSelectBoundTemplateInitialValueNotArray"));
  }

  const [optionValues, mapOptionLabel] = useMemo(
    () => getDropdownValuesLabels({ dropDownItems, customDropdownItemsFilter }),
    [customDropdownItemsFilter, dropDownItems],
  );

  const getOptionLabel = useCallback(
    (value: unknown) => {
      return mapOptionLabel.get(value as string | number) || "";
    },
    [mapOptionLabel],
  );

  const [innerValue, setInnerValue] = useState<number[]>(initial || []);

  const {
    field: { value: formValue, onChange, ...restField },
    fieldState: { error },
  } = useController({ name: name || "", control });

  const sortedValue = useMemo(() => {
    const currentSelection =
      innerValue.length > 0 ? innerValue : (formValue as number[]) || [];
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [innerValue, formValue, getOptionLabel, disableSorting]);

  return name && !isLoading ? (
    <FormControl fullWidth error={!!error} className={className}>
      <Autocomplete
        {...restField}
        multiple
        data-e2e-id={`multi_select_${name}`}
        options={optionValues || []}
        value={sortedValue}
        onChange={(_event, newValue) => {
          if (!newValue) {
            return;
          }

          setInnerValue(newValue as number[]);
          onChange(newValue);
        }}
        getOptionLabel={getOptionLabel}
        disableCloseOnSelect
        renderInput={params => (
          <TextField
            {...params}
            error={!!error}
            label={formLabel}
            required={ValidationHelpers.hasRequiredRule(formValidation)}
            fullWidth
          />
        )}
      />
      {/*TODO: add translation solution*/}
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  ) : null;
};
